import React, { useMemo } from "react";
import classNames from "classnames";
import { getProjectCoverLetter } from "../ProjectCard/getProjectCoverLetter";
import { CoverRoot } from "./styles";

interface ProjectCoverProps {
  imageUrl?: string | null;
  title?: string | null;
  size?: "card" | "mini";
  showTitle?: boolean;
  tinted?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const BODY_LINE_COUNT = 13;
const TITLED_LINE_COUNT = 3;
const HEAD_LINE_COUNT = 3;

function hashTitle(title: string): number {
  let hash = 2166136261;
  for (const char of title) {
    hash = Math.imul(hash ^ char.codePointAt(0)!, 16777619);
  }
  return hash >>> 0;
}

const COVER_TINTS = [
  { bg: "#f2eadb", ink: "#3d352b" },
  { bg: "#e4ebe0", ink: "#2f3a32" },
  { bg: "#dfe8ef", ink: "#2c3742" },
  { bg: "#f1e0dc", ink: "#47322f" },
  { bg: "#e7e1ee", ink: "#3a3347" },
  { bg: "#eee5d0", ink: "#41382a" },
  { bg: "#e2e9e7", ink: "#2d3a39" },
  { bg: "#f3edd2", ink: "#443d27" },
];

function getLineWidths(title: string): number[] {
  let seed = hashTitle(title);
  const widths: number[] = [];
  for (let i = 0; i < HEAD_LINE_COUNT + BODY_LINE_COUNT; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    widths.push(78 + (seed % 23));
  }
  widths[HEAD_LINE_COUNT + BODY_LINE_COUNT - 1] = 38 + (seed % 20);
  return widths;
}

function getTintStyle(title: string): React.CSSProperties {
  const tint = COVER_TINTS[hashTitle(title) % COVER_TINTS.length];
  return { "--cover-tint": tint.bg, "--cover-ink": tint.ink } as React.CSSProperties;
}

export default function ProjectCover({ imageUrl, title, size = "card", showTitle = false, tinted = true, className, children }: ProjectCoverProps) {
  const widths = useMemo(() => getLineWidths(title || ""), [title]);
  const line = (index: number) => (
    <div
      key={index}
      className="cover-line"
      style={{ width: `${widths[index]}%` }}
    />
  );

  return (
    <CoverRoot
      className={classNames("project-cover", className, { "has-image": !!imageUrl, mini: size === "mini", tinted: tinted && showTitle && !imageUrl })}
      style={tinted && showTitle && !imageUrl ? getTintStyle(title || "") : undefined}
    >
      {imageUrl ? (
        <img
          className="cover-image"
          src={imageUrl}
          alt=""
          draggable={false}
        />
      ) : showTitle ? (
        <div
          className="cover-page titled"
          aria-hidden="true"
        >
          <span className="cover-title">{title}</span>
          {Array.from({ length: TITLED_LINE_COUNT }, (_, i) => line(HEAD_LINE_COUNT + i))}
        </div>
      ) : (
        <div
          className="cover-page"
          aria-hidden="true"
        >
          <div className="cover-page-head">
            <span className="cover-drop-cap">{getProjectCoverLetter(title)}</span>
            <div className="cover-page-head-lines">{Array.from({ length: HEAD_LINE_COUNT }, (_, i) => line(i))}</div>
          </div>
          {Array.from({ length: BODY_LINE_COUNT }, (_, i) => line(HEAD_LINE_COUNT + i))}
        </div>
      )}
      <span className="cover-shine" />
      {children}
    </CoverRoot>
  );
}
